import { logger } from './logger';
/**
 * Servicio de notificaciones nativas del escritorio (Browser Notification API).
 * Estas notificaciones aparecen a nivel de sistema operativo, fuera del navegador.
 */

const PERMISSION_REQUESTED_KEY = 'savidna-desktop-notif-requested';
export interface DesktopNotificationOptions {
  title: string;
  body: string;
  icon?: string;
  tag?: string;
  requireInteraction?: boolean;
  onClick?: () => void;
}

/**
 * Verifica si el navegador soporta notificaciones de escritorio
 */
export function isDesktopNotificationSupported(): boolean {
  return typeof window !== 'undefined' && 'Notification' in window;
}

/**
 * Devuelve el estado actual del permiso: 'granted' | 'denied' | 'default'
 */
export function getPermissionStatus(): NotificationPermission | 'unsupported' {
  if (!isDesktopNotificationSupported()) return 'unsupported';
  return Notification.permission;
}

/**
 * Solicita permiso al usuario para mostrar notificaciones de escritorio.
 * Sólo se pregunta una vez por sesión del navegador.
 */
export async function requestDesktopPermission(): Promise<NotificationPermission> {
  if (!isDesktopNotificationSupported()) return 'denied';
  if (Notification.permission === 'granted') return 'granted';
  if (Notification.permission === 'denied') return 'denied';
  try {
    const result = await Notification.requestPermission();
    localStorage.setItem(PERMISSION_REQUESTED_KEY, '1');
    return result;
  } catch (err) {
    logger.warn('[DesktopNotif] Error solicitando permiso:', err);
    return 'denied';
  }
}

/**
 * Solicita permiso sólo si no se ha pedido antes en este navegador.
 * Útil para llamar al iniciar sesión sin molestar al usuario en cada carga.
 */
export async function requestDesktopPermissionOnce(): Promise<NotificationPermission> {
  if (!isDesktopNotificationSupported()) return 'denied';
  if (Notification.permission !== 'default') return Notification.permission;
  return requestDesktopPermission();
}

/**
 * Muestra una notificación de escritorio.
 * Si el permiso no está concedido, no hace nada (no falla).
 */
export function sendDesktopNotification(options: DesktopNotificationOptions): Notification | null {
  if (!isDesktopNotificationSupported()) {
    logger.log('[DesktopNotif] No soportado en este navegador');
    return null;
  }
  if (Notification.permission !== 'granted') {
    logger.log('[DesktopNotif] Permiso no concedido:', Notification.permission);
    return null;
  }
  try {
    const notification = new Notification(options.title, {
      body: options.body,
      icon: options.icon || '/favicon.ico',
      tag: options.tag,
      requireInteraction: options.requireInteraction || false,
      badge: options.icon || '/favicon.ico'
    });
    if (options.onClick) {
      notification.onclick = e => {
        e.preventDefault();
        window.focus();
        options.onClick?.();
        notification.close();
      };
    } else {
      notification.onclick = () => {
        window.focus();
        notification.close();
      };
    }

    // Auto-cerrar después de 8 segundos (a menos que requireInteraction esté activo)
    if (!options.requireInteraction) {
      setTimeout(() => notification.close(), 8000);
    }
    return notification;
  } catch (err) {
    logger.error('[DesktopNotif] Error mostrando notificación:', err);
    return null;
  }
}